import { useUrl } from '@/hooks/use-url';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import Slider from 'react-slick';
import { CVtemplates, Lettertemplates } from '@/constants/templates';
import redraft from 'redraft';
import slugify from 'slugify';
import BeforeAfter from '../before-after';
import { NewLineDecorator } from './decorators/NewLine';
import { YearDecorator } from './decorators/Year';

const Renderer = ({ content_raw, site }) => {
  const { getAppUrl } = useUrl();
  const router = useRouter();
  const ARIA_LABELS = {
    fr_FR: {
      'clip-a-retenir': `Ce qu'il faut retenir`,
      'clip-a-eviter': `À éviter`,
      'clip-coach': `Challenges du coach`,
    },
  };

  const renderers = {
    inline: {
      BOLD: (children, { key }) => <b key={key}>{children}</b>,
      ITALIC: (children, { key }) => <i key={key}>{children}</i>,
      UNDERLINE: (children, { key }) => <u key={key}>{children}</u>,
    },
    blocks: {
      'unstyled': (children, { keys, data }) =>
        children.map((child, i) => {
          if (data[i].textAlign && data[i].textAlign != 'align-left') {
            return (
              <p className={data[i].textAlign} key={data[i].key}>
                {child}
              </p>
            );
          } else {
            return <p key={data[i].key}>{child}</p>;
          }
        }),

      'header-two': (children, { keys, data }) =>
        children.map((child, i) => (
          <h2 key={data[i].key} id={slugify(child.join(''), { remove: /[*+~.,()'"!?:@]/g })}>
            {child}
          </h2>
        )),
      'header-three': (children, { keys, data }) =>
        children.map((child, i) => (
          <h3 key={data[i].key} id={slugify(child.join(''), { remove: /[*+~.,()'"!?:@]/g })}>
            {child}
          </h3>
        )),
      'header-four': (children, { keys, data }) =>
        children.map((child, i) => (
          <h4 key={data[i].key} id={slugify(child.join(''), { remove: /[*+~.,()'"!?:@]/g })}>
            {child}
          </h4>
        )),
      'header-five': (children, { keys, data }) =>
        children.map((child, i) => (
          <h5 key={data[i].key} id={slugify(child.join(''), { remove: /[*+~.,()'"!?:@]/g })}>
            {child}
          </h5>
        )),
      'unordered-list-item': (children, { keys, data }) => (
        <ul key={data.map((d) => d.key).join('-')}>
          {children.map((child, i) => (
            <li key={data[i].key}>{child}</li>
          ))}
        </ul>
      ),
      'ordered-list-item': (children, { keys, data }) => (
        <ol key={data.map((d) => d.key).join('-')}>
          {children.map((child, i) => (
            <li key={data[i].key}>{child}</li>
          ))}
        </ol>
      ),
      'clip-important': (children, { keys, data }) =>
        children.map((child, i) => (
          <p key={data[i].key} className="clip-important">
            {child}
          </p>
        )),
      'clip-a-savoir': (children, { keys, data }) =>
        children.map((child, i) => (
          <p key={data[i].key} className="clip-a-savoir">
            {child}
          </p>
        )),
      'clip-a-retenir': (children, { keys, data }) => (
        <ul
          aria-label={ARIA_LABELS['fr_FR']['clip-a-retenir']}
          key={data.map((d) => d.key).join('-')}
          className="clip-a-retenir"
        >
          {children.map((child, i) => (
            <li key={data[i].key}>{child}</li>
          ))}
        </ul>
      ),
      'clip-a-eviter': (children, { keys, data }) =>
        children.map((child, i) => (
          <p aria-label={ARIA_LABELS['fr_FR']['clip-a-eviter']} key={data[i].key} className="clip-a-eviter">
            {child}
          </p>
        )),
      'clip-coach': (children, { keys, data }) => (
        <ol className="clip-coach" aria-label={ARIA_LABELS['fr_FR']['clip-coach']} key={keys.join('|')}>
          {children.map((child, i) => (
            <li key={data[i].key}>{child}</li>
          ))}
        </ol>
      ),
      'clip-rh': (children, { keys, data }) =>
        children.map((child, i) => (
          <figure className="clip-rh" key={data[i].key}>
            <blockquote>{child}</blockquote>
            {data[i].author && (
              <figcaption className="quote-author">
                <b>{data[i].author.fullname}</b>
                <br />
                <span>{data[i].author.job}</span>
              </figcaption>
            )}
          </figure>
        )),
      'clip-exemple-text': (children, { keys, data }) => (
        <div className="clip-exemple" key={data.map((d) => d.key).join('-')}>
          {children.map((child, i) => {
            if (data[i].textAlign && data[i].textAlign != 'align-left') {
              return (
                <p className={data[i].textAlign} key={data[i].key}>
                  {child}
                </p>
              );
            } else {
              return <p key={data[i].key}>{child}</p>;
            }
          })}
        </div>
      ),
      'atomic': (children, { keys, data }) =>
        children.map((child, i) => (
          <p className="image" key={data[i].key}>
            {child}
          </p>
        )),
      'table': (children, { keys, data }) =>
        children.map((child, i) => {
          let { table } = data[i];
          let { caption, thead, tbody } = table ? JSON.parse(table) : {};

          return (
            <div className="table" key={data[i].key}>
              <table>
                {caption && <caption>{caption}</caption>}
                <thead>
                  {thead &&
                    thead.map((row, i) => {
                      return (
                        <tr key={`thead_${i}`}>
                          {row.map((th, j) => {
                            return (
                              <th
                                key={`th_${i}_${j}`}
                                dangerouslySetInnerHTML={{ __html: th.replace(/\n/g, '<br />') }}
                              ></th>
                            );
                          })}
                        </tr>
                      );
                    })}
                </thead>
                <tbody>
                  {tbody &&
                    tbody.map((row, i) => {
                      return (
                        <tr key={`tbody_${i}`}>
                          {row.map((td, j) => {
                            return (
                              <td
                                key={`td_${i}_${j}`}
                                dangerouslySetInnerHTML={{ __html: td.replace(/\n/g, '<br />') }}
                              ></td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          );
        }),
      'cta-avant-apres': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-avant-apres">
              {data[i].title && <p className="title">{data[i].title}</p>}
              {data[i].subtitle && <p className="subtitle">{data[i].subtitle}</p>}
              <BeforeAfter cta_resumes_before_after={data[i].cta_resumes_before_after} site={site} />
              <img
                className="bg"
                alt="background"
                src="/assets/images/backgrounds/cta-before-after-background.svg"
                width="650"
                height="550"
                loading="lazy"
              />
              <Link href={getAppUrl(`/create/resume?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-banniere-modeles': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-banniere-modeles">
              {data[i].cta_resumes_templates_banner &&
                data[i].cta_resumes_templates_banner.image &&
                data[i].cta_resumes_templates_banner.image.url && (
                  <img
                    alt={data[i].cta_resumes_templates_banner.image.alt}
                    src={data[i].cta_resumes_templates_banner.image.url}
                    loading="lazy"
                    width={data[i].cta_resumes_templates_banner.image.width}
                    height={data[i].cta_resumes_templates_banner.image.height}
                  />
                )}
              <p className="title">{data[i].title}</p>
              <p className="subtitle">{data[i].subtitle}</p>

              <Link href={getAppUrl(`/create/resume?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-slider-cv': (children, { keys, data }) =>
        children.map((child, i) => {
          const sliderRef = useRef<Slider | null>(null);
          const getImageUrl = (template: string) => {
            return `https://mmc-preview-s3-storage.s3.eu-central-1.amazonaws.com/templates/resumes/${site?.html_lang}/${template.toLowerCase()}.jpeg`;
          };

          const images = CVtemplates.map(getImageUrl);

          return (
            <div key={data[i].key} className="cta-slider-cv">
              <div className="slider-container">
                <p className="title">{data[i].title}</p>
                <div className="slick-slider-container">
                  <Slider
                    ref={sliderRef}
                    dots={false}
                    infinite={true}
                    slidesToShow={1}
                    slidesToScroll={1}
                    centerPadding="0"
                    arrows={false}
                  >
                    {images.map((imageUrl, i) => (
                      <div className="slide" key={i}>
                        <div className="preview">
                          <img src={imageUrl} alt="CV" width={350} height={495} loading="lazy" />
                        </div>
                        <div className="cta-container">
                          <Link
                            href={getAppUrl(
                              `/create/resume?ref=${router.asPath}&template=${CVtemplates.find(t => t.toLowerCase() === imageUrl.split('/').pop()?.split('.')?.[0])}`
                            )}
                          >
                            <button className="cta">
                              <span className="cta-text">Choisir ce modèle</span>
                              <img
                                src="/assets/icons/arrow-right-white.svg"
                                alt="arrow right"
                                width="18"
                                height="18"
                                loading="lazy"
                              />
                            </button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
              <div className="slider-nav">
                <div className="slider-nav-btn" onClick={() => sliderRef.current?.slickPrev()}>
                  <img alt="arrow left" src="/assets/icons/arrow-left.svg" width="28" height="28" loading="lazy" />
                </div>
                <div className="slider-nav-btn" onClick={() => sliderRef.current?.slickNext()}>
                  <img alt="arrow right" src="/assets/icons/arrow-right.svg" width="28" height="28" loading="lazy" />
                </div>
              </div>
            </div>
          );
        }),
      'cta-slider-lm': (children, { keys, data }) =>
        children.map((child, i) => {
          const sliderRef = useRef<Slider | null>(null);
          const getImageUrl = (template: string) => {
            return `https://mmc-preview-s3-storage.s3.eu-central-1.amazonaws.com/templates/letters/${site?.html_lang}/${template.toLowerCase()}.jpeg`;
          };

          const images = Lettertemplates.map(getImageUrl);

          return (
            <div key={data[i].key} className="cta-slider-lm">
              <div className="slider-container">
                <p className="title">{data[i].title}</p>
                <div className="slick-slider-container">
                  <Slider
                    ref={sliderRef}
                    dots={false}
                    infinite={true}
                    slidesToShow={1}
                    slidesToScroll={1}
                    centerPadding="0"
                    arrows={false}
                  >
                    {images.map((imageUrl, i) => (
                      <div className="slide" key={i}>
                        <div className="preview">
                          <img src={imageUrl} alt="Lettre de motivation" width={350} height={495} loading="lazy" />
                        </div>
                        <div className="cta-container">
                          <Link
                            href={getAppUrl(
                              `/create/cover-letter?ref=${router.asPath}&template=${Lettertemplates.find(t => t.toLowerCase() === imageUrl.split('/').pop()?.split('.')?.[0])}`
                            )}
                          >
                            <button className="cta">
                              <span className="cta-text">Choisir ce modèle</span>
                              <img
                                alt="arrow right"
                                src="/assets/icons/arrow-right-white.svg"
                                width="18"
                                height="18"
                                loading="lazy"
                              />
                            </button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
              <div className="slider-nav">
                <div className="slider-nav-btn" onClick={() => sliderRef.current?.slickPrev()}>
                  <img alt="arrow left" src="/assets/icons/arrow-left.svg" width="28" height="28" loading="lazy" />
                </div>
                <div className="slider-nav-btn" onClick={() => sliderRef.current?.slickNext()}>
                  <img alt="arrow right" src="/assets/icons/arrow-right.svg" width="28" height="28" loading="lazy" />
                </div>
              </div>
            </div>
          );
        }),
      'cta-bouton-simple-cv': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-bouton-simple">
              <Link href={getAppUrl(`/create/resume?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-bouton-simple-lm': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-bouton-simple">
              <Link href={getAppUrl(`/create/cover-letter?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-bouton-simple-avec-titre-cv': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-bouton-simple-avec-titre">
              {data[i].title && <p className="text">{data[i].title}</p>}
              <Link href={getAppUrl(`/create/resume?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-bouton-simple-avec-titre-lm': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-bouton-simple-avec-titre">
              {data[i].title && <p className="text">{data[i].title}</p>}
              <Link href={getAppUrl(`/create/cover-letter?ref=${router.asPath}`)}>
                <button className="cta">
                  <span className="cta-text">{data[i].cta}</span>
                  <img
                    alt="arrow right"
                    src="/assets/icons/arrow-right-white.svg"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>
          );
        }),
      'cta-focus-competences': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'cta-focus-cv': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'cta-focus-accroche': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'cta-focus-coordonnees': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'cta-focus-formations-diplomes': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'cta-focus-langues': (children, { keys, data }) =>
        children.map((child, i) => {
          return (
            <div key={data[i].key} className="cta-focus">
              {data[i] && data[i].image && (
                <img
                  alt={data[i].image.alt}
                  src={data[i].image.url}
                  width={data[i].image.width / 2}
                  height={data[i].image.height / 2}
                />
              )}
              <p>{child}</p>
            </div>
          );
        }),
      'youtube-video': (children, { keys, data }) =>
        children.map((child, i) => {
          let { video } = data[i];
          let { videoId } = video ? JSON.parse(video) : {};
          return (
            <div key={data[i].key} className="yt-video">
              {videoId && (
                <iframe
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          );
        }),
    },
    /**
     * Entities receive children and the entity data
     */
    entities: {
      LINK: (children, data, { key }) => {
        if (data.href && data.href.trim().length > 0) {
          return (
            <Link
              key={key}
              href={data.href}
              target={data.target ? data.target : null}
              rel={data.target ? 'nofollow' : undefined}
            >
              {children}
            </Link>
          );
        } else {
          return children;
        }
      },
      JSLINK: (children, data, { key }) => {
        if (data.href && data.href.length > 0) {
          return (
            <Link key={key} href={data.href + (data.href.indexOf('app.makemycv') > -1 ? '?ref=' + router.asPath : '')}>
              <span className="cta-link">{children}</span>
            </Link>
          );
        } else {
          return children;
        }
      },
      IMAGE: (children, data, { key }) => (
        <>
          <img key={key} src={data.src} alt={data.alt} width={data.width} height={data.height} loading="lazy" />
        </>
      ),
    },
    decorators: [NewLineDecorator, YearDecorator],
  };

  if (!content_raw) {
    return <></>;
  }

  const blocks: typeof content_raw.blocks = [];

  // Retirer les blocks "unstyled" dont le texte est vide
  content_raw.blocks.forEach((element) => {
    if (element.type === 'unstyled' && element.text === '') {
    } else {
      blocks.push(element);
    }
  });

  content_raw.blocks = blocks;

  return redraft(content_raw, renderers, { blockFallback: 'unstyled' });
};

export default Renderer;
